import {CombinedColumnsT, Database, DotNotationPathT, FilterT, SupabaseFilterT} from '@tnc/shared';
import {useState} from 'react';

import {brandT} from '@/lib/types/brand.types';
import {distributorT} from '@/lib/types/distributor.types';
import {productWithBrandT} from '@/lib/types/product.types';
import {BasicDistributorT} from '@/lib/types/user.types';

import {useDataQuery} from '@/hooks';

import {useAuth} from '@/providers/components/authProvider';

import {SearchAndSelectComponent} from '@/components';
import Page from '@/components/Page';

import {useCreateRateSheet} from '../providers/createRateSheetProvider';
import RateSheetDetailsComponent from './rateSheetDetailsComponent';
import RateSheetFormRowComponent from './rateSheetFormRowComponent';
import logo from '/images/logo/logo.png';

// import RateSheetDetailsComponent from './rateSheetDetailsComponent';
// import RateSheetFormRowComponent from './rateSheetFormRowComponent';

/**
 * Create rate sheet form
 * @returns {JSX.Element}
 */
function CreateRateSheetForm() {
  const {
    userAccounts: {distributors: userDistributors},
  } = useAuth();

  const {
    rates,
    selectedDistributor,
    selectedMarket,
    validateRateSheet,
    setAcknowledging,
    acknowledging,
    updateRate,
    deleteRate,
    setSelectedDistributor,
    setSelectedMarket,
    addRate,
    title,
    setTitle,
    mode,
    resetRates,
  } = useCreateRateSheet();

  // state for search modals
  const [showBrandSearch, setShowBrandSearch] = useState(false);
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [showDistributorSearch, setShowDistributorSearch] = useState(false);
  const [showMarketSearch, setShowMarketSearch] = useState(false);
  const [distributors, setDistributors] = useState<BasicDistributorT[]>(userDistributors || []);
  const [marketSearchTerm, setMarketSearchTerm] = useState('');
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [brandSearchTerm, setBrandSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState<brandT | null>(null);
  const [productFilters, setProductFilters] = useState<FilterT<ProductTableT>[]>([]);

  /**
   * Markets data based on selected distributor
   */
  const {data: marketsData} = useDataQuery({
    table: 'Market_Distributor',
    columns: ['*', ['Market', ['*']]],
    filters: [
      {
        column: 'fk_distributor_id',
        operator: 'eq',
        value: selectedDistributor?.distributor_id,
      },
    ],
    searchColumn: 'Market.market_name',
    searchTerm: marketSearchTerm,
  });

  /**
   * Getting brands data based on selected distributor
   */
  const {data: brandsData} = useDataQuery({
    table: 'Distributor_Brand',
    columns: ['*', ['Brand', ['*']]],
    filters: [
      {
        column: 'fk_distributor_id',
        operator: 'eq',
        value: selectedDistributor?.distributor_id,
      },
    ],
    searchColumn: 'Brand.brand_name',
    searchTerm: brandSearchTerm,
  });

  type ProductTableT = 'Product';

  const handleFilterChange = (field: DotNotationPathT<CombinedColumnsT<ProductTableT, ['*']>>, value: string) => {
    if (value === '') {
      setProductFilters(prev => prev.filter(f => f.column !== field));
    } else {
      const newFilter: FilterT<ProductTableT> = {
        column: field,
        operator: 'eq',
        value,
      };
      setProductFilters(prev => [...prev.filter(f => f.column !== field), newFilter]);
    }
  };

  /**
   * Building product filters
   */
  const typedProductFilters = productFilters
    .filter((filter): filter is typeof filter => {
      return typeof filter.column === 'string';
    })
    .map(filter => ({
      column: filter.column as keyof Database['public']['Tables']['Product']['Row'],
      operator: filter.operator as SupabaseFilterT,
      value: filter.value,
    }));

  /**
   * Getting products data based on the selected brand
   */
  const {data: productsData} = useDataQuery({
    table: 'Product',
    columns: ['*', ['Brand', ['*']]],
    filters: [
      {
        column: 'fk_brand_id',
        operator: 'eq',
        value: selectedBrand?.brand_id,
      },
      {
        not: {
          column: 'product_id',
          operator: 'in',
          value: rates.map(rate => rate.fk_product_id ?? 0).filter(Boolean),
        },
      },
      ...typedProductFilters,
    ],
    searchColumn: 'product_name',
    searchTerm: productSearchTerm,
    enabled: !!selectedBrand,
  });

  /**
   * Handles distributor search by filtering the userDistributors state
   */
  const handleDistributorSearch = (searchTerm: string) => {
    setDistributors(
      userDistributors?.filter(distributor =>
        distributor.distributor_name.toLowerCase().includes(searchTerm.toLowerCase())
      ) || []
    );
  };

  /**
   * Handles brand search by filtering the brandsData state
   */
  const handleBrandSearch = (searchTerm: string) => {
    setBrandSearchTerm(searchTerm);
  };

  /**
   * Handles product search
   */
  const handleProductSearch = (searchTerm: string) => {
    setProductSearchTerm(searchTerm);
  };

  /**
   * Handles market search for selected distributor
   */
  const handleMarketSearch = async (searchTerm: string) => {
    if (!selectedDistributor) {
      alert('Please select a distributor first');
      return;
    }

    setMarketSearchTerm(searchTerm);
  };

  const handleReviewRates = () => {
    const isValid = validateRateSheet();
    if (isValid) {
      setAcknowledging(true);
    } else {
      // scroll to top
      window.scrollTo({top: 0, behavior: 'smooth'});
    }
  };

  if (acknowledging) {
    return <RateSheetDetailsComponent />;
  }

  return (
    <Page currentPage="Create Rate Sheet">
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 xl:col-span-12">
          <div className="box">
            {/* HEADER */}
            <div className="box-header flex flex-col items-start">
              {/* Logo and Date/Time */}
              <div className="h5 mb-4 flex items-center">
                <div>
                  <img src={logo} alt="" className="w-10" />
                </div>
                <div className="ms-2">
                  <span className="form-control !rounded-md !p-0">{new Date().toLocaleDateString()}</span>
                </div>
                <div className="mx-2">:</div>
                <div>
                  <span className="form-control !rounded-md !p-0">{new Date().toLocaleTimeString()}</span>
                </div>
              </div>

              {/* SELECTION AREA */}
              <div className="mb-4 flex items-center space-x-4">
                <div className="flex items-center">
                  <span className="mr-2">Distributor:</span>
                  {selectedDistributor ? (
                    <span className="mr-2">{selectedDistributor.distributor_name}</span>
                  ) : (
                    <span className="mr-2 text-gray-400">No distributor selected</span>
                  )}
                  {mode !== 'edit' && (
                    <button
                      aria-label="search"
                      className="ti-btn ti-btn-sm ti-btn-light"
                      onClick={() => setShowDistributorSearch(true)}
                    >
                      <i className="ri-search-line"></i>
                    </button>
                  )}
                </div>

                <div className="flex items-center">
                  <span className="mr-2">Market:</span>
                  {selectedMarket ? (
                    <span className="mr-2">{selectedMarket.market_name}</span>
                  ) : (
                    <span className="mr-2 text-gray-400">No market selected</span>
                  )}
                  {mode !== 'edit' && (
                    <button
                      aria-label="search"
                      className="ti-btn ti-btn-sm ti-btn-light"
                      onClick={() => {
                        if (!selectedDistributor) {
                          alert('Please select a distributor first');
                          return;
                        }
                        setShowMarketSearch(true);
                      }}
                    >
                      <i className="ri-search-line"></i>
                    </button>
                  )}
                </div>

                <div className="flex items-center">
                  <span className="mr-2">Brand:</span>
                  {selectedBrand ? (
                    <span className="mr-2">{selectedBrand.brand_name}</span>
                  ) : (
                    <span className="mr-2 text-gray-400">No brand selected</span>
                  )}
                  <button
                    aria-label="search"
                    className="ti-btn ti-btn-sm ti-btn-light"
                    onClick={() => {
                      if (!selectedMarket) {
                        alert('Please select a market first');
                        return;
                      }

                      if (!selectedDistributor) {
                        alert('Please select a distributor first');
                        return;
                      }

                      setShowBrandSearch(true);
                    }}
                  >
                    <i className="ri-search-line"></i>
                  </button>
                </div>

                <div className="flex items-center">
                  {/* add product button search icon */}
                  <span className="mr-2">Product:</span>
                  <button
                    aria-label="search"
                    className="ti-btn ti-btn-sm ti-btn-light"
                    onClick={() => {
                      if (!selectedBrand) {
                        alert('Please select a brand first');
                        return;
                      }
                      setShowProductSearch(true);
                    }}
                  >
                    <i className="ri-add-circle-line"></i>
                  </button>
                </div>

                {/* Search Components */}
                <SearchAndSelectComponent<BasicDistributorT>
                  title="Search and Select Distributor"
                  isVisible={showDistributorSearch}
                  onClose={() => setShowDistributorSearch(false)}
                  onSelect={item => {
                    setSelectedDistributor(item as unknown as distributorT);
                    setSelectedMarket(null);
                    setShowDistributorSearch(false);
                    resetRates();
                  }}
                  onSearch={handleDistributorSearch}
                  results={distributors}
                  mapStructure={{
                    displayFields: [
                      {
                        title: 'Distributor Name',
                        field: 'distributor_name',
                      },
                    ],
                    valueField: 'distributor_id',
                  }}
                />

                <SearchAndSelectComponent
                  title="Search and Select Market"
                  isVisible={showMarketSearch}
                  onClose={() => setShowMarketSearch(false)}
                  onSelect={item => {
                    setSelectedMarket(item.Market);
                    setShowMarketSearch(false);
                    resetRates();
                  }}
                  onSearch={handleMarketSearch}
                  results={marketsData?.data || []}
                  mapStructure={{
                    displayFields: [
                      {
                        title: 'Market Name',
                        field: 'Market.market_name',
                      },
                    ],
                    valueField: 'Market.market_id',
                  }}
                />

                {/* Brand Search*/}
                <SearchAndSelectComponent
                  title="Search and Select Brand"
                  isVisible={showBrandSearch}
                  onClose={() => setShowBrandSearch(false)}
                  onSelect={item => {
                    setSelectedBrand(item.Brand);
                    setShowBrandSearch(false);
                  }}
                  onSearch={handleBrandSearch}
                  results={brandsData?.data || []}
                  mapStructure={{
                    displayFields: [
                      {
                        title: 'Brand Name',
                        field: 'Brand.brand_name',
                      },
                    ],
                    valueField: 'Brand.brand_id',
                  }}
                />

                {/* Product Search*/}
                <SearchAndSelectComponent<productWithBrandT>
                  title="Search and Select Product"
                  overrideClose={true}
                  isVisible={showProductSearch}
                  onClose={() => setShowProductSearch(false)}
                  onSelect={item => {
                    // Add new rate with selected product
                    addRate({
                      Brand: item.Brand,
                      fk_brand_id: item.Brand.brand_id ?? 0,
                      fk_product_id: item.product_id ?? 0,
                      msrp: null,
                      Product: item,
                      tnc_price: 0,
                      wholesale_price: 0,
                    });
                  }}
                  onSearch={handleProductSearch}
                  results={
                    productsData?.data.filter(
                      product => !rates.some(rate => rate.fk_product_id === product.product_id)
                    ) || []
                  }
                  mapStructure={{
                    displayFields: [
                      {
                        isImage: true,
                        field: 'product_image_url',
                        title: 'Image',
                      },
                      {
                        title: 'Product Name',
                        field: 'product_name',
                      },
                      {
                        title: 'Category',
                        field: 'product_category',
                      },
                    ],
                    valueField: 'product_id',
                  }}
                  filterConfig={[
                    {
                      type: 'dropdown',
                      label: 'Category',
                      field: 'product_category',
                      options: [
                        {label: 'Concentrates', value: 'Concentrate'},
                        {label: 'Category 2', value: 'category2'},
                        // Add your categories here
                      ],
                    },
                    {
                      type: 'dropdown',
                      label: 'Sub Category',
                      field: 'product_subcategory',
                      options: [
                        {label: 'Cartridge', value: 'Cartridge'},
                        {label: 'Edibles', value: 'Edibles'},
                        // Add your sub categories here
                      ],
                    },
                  ]}
                  onFilterChange={(field, value) =>
                    handleFilterChange(field as DotNotationPathT<CombinedColumnsT<ProductTableT, ['*']>>, value)
                  }
                />
              </div>

              {/* title input */}
              <div>
                <label htmlFor="title" className="mr-2">
                  Title:
                </label>
                <div className="search-form h-full">
                  <input
                    className="form-control w-full!rounded-sm me-2"
                    type="text"
                    value={title}
                    placeholder="Input Title for Rate Sheet"
                    aria-label=".form-control-sm example"
                    onChange={e => setTitle(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="box-body">
              {selectedDistributor && selectedMarket ? (
                <div className="grid grid-cols-12 gap-y-6 sm:gap-x-6">
                  <div className="col-span-12 xl:col-span-12">
                    <div className="table-responsive">
                      <table className="mt-3 table whitespace-nowrap border dark:border-defaultborder/10">
                        <thead>
                          <tr>
                            <th scope="row">Product Name</th>
                            <th scope="row">Brand Name</th>
                            <th scope="row">Category</th>
                            <th scope="row">Subcategory</th>
                            <th scope="row">Suggested Wholesale*</th>
                            <th scope="row">TNC Price*</th>
                            <th scope="row">Min Quantity*</th>
                            <th scope="row">Factored Price*</th>
                            <th scope="row">MSRP </th>
                            <th scope="row">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rates.map((rate, index) => (
                            <RateSheetFormRowComponent
                              key={`${rate.rate_id || rate.created_by}-${index}`}
                              rate={rate}
                              onUpdateRate={data => updateRate(index, data)}
                              onDelete={() => deleteRate(index)}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {/* FOOTER */}
                  <div className="col-span-12 xl:col-span-12">
                    <div className="flex justify-end">
                      <table className="table-sm table-borderless mb-0 table whitespace-nowrap dark:border-defaultborder/10">
                        <tbody>
                          <tr>
                            <th scope="row">
                              <div className="text-sm font-semibold">Potential Profit :</div>
                            </th>
                            <td>
                              <span
                                className="form-control !rounded-md !p-0 font-bold"
                                style={{
                                  color: 'green',
                                }}
                              >
                                $0
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              <div className="text-sm font-semibold">Market Value :</div>
                            </th>
                            <td>
                              <span className="form-control !rounded-md !p-0">$0</span>
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              <div className="text-sm font-semibold">Sub Total :</div>
                            </th>
                            <td>
                              <span className="form-control !rounded-md !p-0">$0</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-gray-500">Please select a distributor and market to get started.</p>
                </div>
              )}
            </div>
            <div className="box-footer text-end">
              {rates.length > 0 && (
                <button type="button" className="ti-btn !mb-0 bg-primary text-white" onClick={handleReviewRates}>
                  Review Rate{rates.length > 1 ? 's' : ''}{' '}
                  <i className="ri-send-plane-2-line ms-1 inline-block align-middle"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
}

export default CreateRateSheetForm;
